'use client';
import { POPUPS } from '@/app/components/popup/constants';
import './styles/@theme';
import { SvgLoader } from '@/app/elements/svg/loader';
import { getGame } from '@/logic/game-info/game-info';
import { useStore } from '@/store/store';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useGlobalContext } from '@/store/contexts/global-context';
import { RefObject, useRef } from 'react';
import { trackGameInfoEvent } from '@/tracking/amplitude/events/general/game-info';
import { getTrackingData } from '@/tracking/amplitude/events/general/game-selected';
import { useDynamicOrderingContext } from '@/app/components/zones/contexts/dynamic-ordering-context';
import { ZONE_SLICE_TYPES } from '@/app/constants/zone';
import { useCategoryTracking } from '@/tracking/snowplow/contexts/category-tracking-context';
import { EVENT_TYPE } from '@/tracking/snowplow/utils';
import { sendGameElementSnowplowEvent } from '@/tracking/snowplow/send-game-element-snowplow-event';
import { getVideoUrl } from '@/logic/game-adapter/game-adapter';
import { getGameInfoVideoPageName } from '@/tracking/amplitude/events/general/game-info-video';

type InfoButtonProps = {
    uid: string;
};

export const InfoButton = ({ uid }: InfoButtonProps) => {
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);
    const { isLoggedIn, isPokerDesktopClient, isAndroidWrapper } = useGlobalContext();
    const showModal = isAndroidWrapper || isLoggedIn;

    const { queuePopup } = useStore((s) => s.popup);
    const { sendEvent } = useStore((state) => state.tracking);
    const { zoneUid, isDynamicOrderingEnabled } = useDynamicOrderingContext();
    const categoryTracking = useCategoryTracking();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const linkRef = useRef<HTMLAnchorElement>(null);

    const sendSnowplowInfoClickEvent = () => {
        if (!categoryTracking.categoryName) return;

        const activeRef = showModal ? buttonRef : linkRef;
        const trackingData = getTrackingData(activeRef as RefObject<HTMLElement>);

        void sendGameElementSnowplowEvent({
            sendEvent,
            eventType: EVENT_TYPE.INFO_ICON_CLICKED,
            eventProps: { infoIconContext: 'game_info' },
            trackingData,
            gameUid: uid,
            gameName: trackingData?.gameName ?? null
        });
    };

    const handleGetDataForGame = async () => {
        sendSnowplowInfoClickEvent();
        const modalOriginTrackingData = getTrackingData(buttonRef as RefObject<HTMLElement>);
        const game = await getGame(uid);
        if (!game) {
            return null;
        }
        const isSimilarGamesZone = modalOriginTrackingData?.zoneType === ZONE_SLICE_TYPES.SIMILAR_GAMES;
        const isVideoAvailable = Boolean(getVideoUrl(game.data?.fullscreen_game_play_trailer));
        const originPageName = getGameInfoVideoPageName(modalOriginTrackingData ?? {});
        const result = trackGameInfoEvent('modalOpened', buttonRef, {
            categoryUid: 'game_info_modal',
            zoneName: 'game_info_modal',
            module: 'game_info_modal',
            isVideoAvailable
        });
        sendEvent([result.event]);
        queuePopup({
            type: POPUPS.GAME_INFO,
            params: {
                ...game,
                ...result.trackingData,
                zoneUid,
                isDynamicOrderingEnabled,
                originModule: isSimilarGamesZone ? 'game_zone' : undefined,
                originZoneUid: modalOriginTrackingData?.zoneUid,
                originZoneName: modalOriginTrackingData?.zoneName,
                originZoneType: modalOriginTrackingData?.zoneType,
                originZoneModule: modalOriginTrackingData?.module,
                originZoneLayout: modalOriginTrackingData?.zoneLayout,
                originZonePosition: modalOriginTrackingData?.zonePosition,
                originGamePosition: modalOriginTrackingData?.gamePosition,
                originPageName
            }
        });
    };

    const handleNavigateGamePage = () => {
        sendSnowplowInfoClickEvent();
        handleShowLoading();
    };

    // Mirrors casino-web's game-tile--layout-default-enhanced::displayIcons rule: the
    // poker desktop client wrapper renders its own game info chrome, so hide the
    // info icon entirely when running inside it.
    if (isPokerDesktopClient) {
        return null;
    }

    return showModal ? (
        <button ref={buttonRef} type="button" className="game__info" onClick={handleGetDataForGame}>
            <SvgLoader iconName="info" />
        </button>
    ) : (
        <Link
            ref={linkRef}
            className="game__info"
            href={`/game/${uid}`}
            onClick={handleNavigateGamePage}
            prefetch={false}>
            <SvgLoader iconName="info" />
        </Link>
    );
};

export const DailyJackpotInfoButton = ({ infoButtonLink }: { infoButtonLink: string }) => {
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);
    const router = useRouter();

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleShowLoading();
        router.push(infoButtonLink);
    };

    if (!infoButtonLink) {
        return null;
    }

    return (
        <button type="button" className="game__info--jackpot" onClick={handleClick}>
            <SvgLoader iconName="info" />
        </button>
    );
};
